import { apiRequest, apiRequestPaged, type PagedResult } from './client'

export interface HrLeaveRow {
  id: string
  employeeId: string
  employeeName: string
  type: string
  status: string
  startDate: string
  endDate: string
  days: number
  reason: string | null
  decidedAt: string | null
  decidedById: string | null
  isEmergency: boolean
  hasDocuments: boolean
  /** True when HR encoded this leave out of system (bypassing the submit gates). */
  isOutOfSystem: boolean
  /** True when this leave was approved while the team was over its concurrent-
   *  absence cap (capacity de-gated 2026-09-24). Drives the "Above capacity" row
   *  badge. Optional for back-compat; the HR list route populates it. */
  approvedOverCapacity?: boolean
  /** When the employee applied — the leave row's creation time (ISO). Null for
   *  leaves brought in by the leave-history import: the old system recorded no
   *  applied date, so there is nothing truthful to show. */
  appliedAt: string | null
  updatedAt: string
  /** Most recent approver nudge / cron reminder (ISO). Drives the bell's 24h
   *  cooldown state. Null = never nudged or not a pending leave. */
  lastNudgedAt: string | null
}

/** Server-side column sort keys for `GET /api/v1/hr/leaves?page=` (numbered path). */
export type HrLeavesSortKey =
  | 'employee_name'
  | 'type'
  | 'status'
  | 'start_date'
  | 'days'
  | 'applied_at'
  | 'updated_at'

/** Filter-card categories (`?flag=`) on the HR Applied Leaves list. */
export type HrLeavesFlag = 'above_capacity' | 'emergency' | 'out_of_system' | 'pending_hr_review'

const HR_LEAVES_FLAGS: ReadonlyArray<HrLeavesFlag> = [
  'above_capacity',
  'emergency',
  'out_of_system',
  'pending_hr_review',
]

/** Narrow an untrusted URL value to a known filter-card category. */
export function asHrLeavesFlag(value: unknown): HrLeavesFlag | null {
  return HR_LEAVES_FLAGS.find((f) => f === value) ?? null
}

/** Per-card counts under the list's other filters (never the card itself). */
export interface HrLeaveCounts {
  aboveCapacity: number
  emergency: number
  outOfSystem: number
  pendingHrReview: number
}

export interface HrLeavesFilters {
  type?: string
  status?: string
  manager?: string
  team_id?: string
  since?: string
  until?: string
  /** Calendar year (`YYYY`) — leaves whose START date falls in it. Combines
   *  with `since`/`until`, which narrow further inside the year. */
  year?: string
  /** Free-text: tokens matched (ILIKE) across employee name + reason, server-side
   *  across ALL leaves. */
  search?: string
  /** Filter-card category — narrows the list to one card's leaves. */
  flag?: HrLeavesFlag
  /** Sliding-window cursor — updated_at of the last row the client holds. */
  before?: string
  /** 1-based offset page. When set, the route returns an enveloped
   *  `{ data, meta }` with an exact `total` (numbered-pagination path). Consumed
   *  by `listHrLeavesPage` below; omit it for the legacy bare-list cursor path. */
  page?: number
  limit?: number
  /** Column sort, applied in SQL over the whole filtered set before paging.
   *  Numbered (`page`) path only; omit for the default updated-desc order. */
  sort?: HrLeavesSortKey
  dir?: 'asc' | 'desc'
}

function buildHrLeavesQuery(filters: HrLeavesFilters): string {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(filters)) {
    if (v != null && v !== '') params.set(k, String(v))
  }
  return params.toString()
}

/** Legacy bare-list (sliding-window / `before` cursor) path. Returns a plain
 *  array — the route omits the envelope when `page` is absent. */
export function listHrLeaves(filters: HrLeavesFilters = {}): Promise<HrLeaveRow[]> {
  const qs = buildHrLeavesQuery(filters)
  return apiRequest<HrLeaveRow[]>(`/api/v1/hr/leaves${qs ? `?${qs}` : ''}`)
}

/** Re-send the "still pending" reminder card to a pending leave's approver.
 *  HR only; the server enforces a 24h cooldown (429 `nudge_cooldown`). */
export function nudgeApprover(leaveId: string): Promise<{ id: string }> {
  return apiRequest<{ id: string }>(`/api/v1/hr/leaves/${leaveId}/nudge`, {
    method: 'POST',
  })
}

/** Numbered-pagination path. Passing `page` flips the route to the enveloped
 *  `OkPaged[HrLeaveRow]` shape, so we read it via `apiRequestPaged` to preserve
 *  `meta.total` for `usePagedList`. */
export function listHrLeavesPage(
  filters: HrLeavesFilters & { page: number },
): Promise<PagedResult<HrLeaveRow>> {
  const qs = buildHrLeavesQuery(filters)
  return apiRequestPaged<HrLeaveRow>(`/api/v1/hr/leaves${qs ? `?${qs}` : ''}`)
}

/** Filter-card counts for the HR Applied Leaves page. Pass the list's filters
 *  minus `flag`, so each card keeps its own number. */
export function getHrLeaveCounts(
  filters: Omit<HrLeavesFilters, 'flag' | 'before' | 'page' | 'limit' | 'sort' | 'dir'>,
): Promise<HrLeaveCounts> {
  const qs = buildHrLeavesQuery(filters)
  return apiRequest<HrLeaveCounts>(`/api/v1/hr/leaves/counts${qs ? `?${qs}` : ''}`)
}
